/**
 * @copyright (C) 2025, rsupport. All rights reserved
 *
 * @license
 * This software and/or source code may be used, copied and/or disseminated only
 * with the written permission of rsupport, or in accordance with the terms
 * and conditions stipulated in the agreement/contract under which the software
 * and/or source code has been supplied by rsupport or its affiliates.
 * Unauthorized use, copying, or dissemination of this file, via any medium, is
 * strictly prohibited, and will constitute an infringement of copyright.
 *
 * @author
 * Jinhyoung Jang (jangjh@rsupport.com)
 *
 * @description
 *
 * @created at 2025-01-15 10:31:22
 **/

import * as DetectRTC from "detectrtc";
import { produce } from "immer";
import has from "lodash-es/has";
import mergeWith from "lodash-es/mergeWith";
import isEmpty from "lodash-es/isEmpty";
import DeviceManagerBase from "./devicemanagerbase";
import useDeviceManager from "./useDeviceManager";
import PermissionManager from "./permissionmanager";
import { PermissionEventCallback } from "./permissionmanager";

const LOGTAG = "#DeviceManager#";

export default class DeviceManager extends DeviceManagerBase {
	static #instance: DeviceManager | null = null;
	public static get instance() {
		if (!DeviceManager.#instance) {
			DeviceManager.#instance = new DeviceManager();
		}
		return DeviceManager.#instance;
	}

	private constructor() {
		super();
	}

	public initialize(): Promise<void> {
		return new Promise<void>((resolve, reject) => {
			DetectRTC.load(async () => {
				try {
					this.install_event_devicechange();
					if (this.options.permission_checking) {
						await this.checking_device_permission(Boolean(this.options.request_permission));
					} else {
						await this.reset_devices();
					}
					if (this.last_error) {
						throw this.last_error;
					}
					resolve();
				} catch (error) {
					reject(error);
				}
			});
		});
	}

	public async checking_device_permission(
		request_permission: boolean,
		event_callback?: PermissionEventCallback | null
	): Promise<PermissionStatus[]> {
		const permissions = await PermissionManager.instance.request();
		if (request_permission && (permissions[0].state === "prompt" || permissions[1].state === "prompt")) {
			const media_constraints: MediaStreamConstraints = {
				...(permissions[0].state === "prompt" ? { video: true } : {}),
				...(permissions[1].state === "prompt" ? { audio: true } : {})
			};
			if (Object.keys(media_constraints).length > 0) {
				event_callback?.before_get_media_stream(media_constraints);
				const stream = await navigator.mediaDevices.getUserMedia(media_constraints).catch((error) => {
					this.last_error = error;
					return null;
				});
				stream?.getTracks().forEach((track) => track.stop());
				event_callback?.after_get_media_stream?.();
				await this.reset_devices();
			}
		} else {
			await this.reset_devices();
		}
		return permissions;
	}

	/**
	 * 카메라가 유효한지 여부를 확인합니다.
	 *
	 * @return {boolean} 카메라가 존재하고 "id" 속성을 포함하는 경우 true를 반환합니다. 그렇지 않으면 false를 반환합니다.
	 */
	public get is_camera_validate(): boolean {
		const { camera } = useDeviceManager.getState();
		return !isEmpty(camera) && has(camera, "id");
	}

	/**
	 * 마이크가 유효한지 여부를 확인하는 속성입니다.
	 *
	 * @return {boolean} 유효한 마이크가 있는 경우 true, 그렇지 않으면 false를 반환합니다.
	 */
	public get is_microphone_validate(): boolean {
		const { microphone } = useDeviceManager.getState();
		return !isEmpty(microphone) && has(microphone, "id");
	}

	/**
	 * 스피커의 유효성을 검사하는 메서드입니다.
	 * 스피커 객체가 비어있지 않으며 "id" 속성을 포함하고 있는지를 확인합니다.
	 *
	 * @return {boolean} 스피커가 유효한 경우 true를 반환하고, 그렇지 않으면 false를 반환합니다.
	 */
	public get is_speaker_validate(): boolean {
		const { speaker } = useDeviceManager.getState();
		return !isEmpty(speaker) && has(speaker, "id");
	}

	/**
	 * 현재 사용 중인 장치를 반환합니다.
	 *
	 * @param {DeviceType.AllDeviceKinds} kind 장치 유형 (예: "videoinput", "audioinput", "audiooutput").
	 * @return {DeviceType.MediaDeviceSetting} 요청된 유형에 해당하는 미디어 장치 설정.
	 */
	public get_current_device(kind: DeviceType.AllDeviceKinds): DeviceType.MediaDeviceSetting {
		const { camera, microphone, speaker } = useDeviceManager.getState();
		return kind === "videoinput" ? camera : kind === "audioinput" ? microphone : speaker;
	}

	/**
	 * 현재 활성 기기를 설정합니다.
	 *
	 * @param {DeviceType.AllDeviceKinds} kind 기기의 종류를 나타내는 값. ("videoinput", "audioinput", "audiooutput" 중 하나)
	 * @param {string} device_id 설정할 기기의 고유 식별자.
	 * @return {void} 반환 값이 없습니다.
	 */
	public set_current_device(kind: DeviceType.AllDeviceKinds, device_id: string): void {
		useDeviceManager.setState((state) =>
			produce(state, (draft) => {
				const name = kind === "videoinput" ? "camera" : kind === "audioinput" ? "microphone" : "speaker";
				draft[name] = {
					...draft[name],
					...this.get_device_with_id(kind, device_id)
				};
				this.use_debugging && console.log(LOGTAG, "set_current_device: ", name, draft[name]);
			})
		);
	}

	public get_device_with_id(kind: DeviceType.AllDeviceKinds, device_id: string): DeviceType.MediaDeviceSetting {
		let result =
			device_id.length > 0 ? this.devices.filter((item) => item.kind === kind && item.id === device_id) : [];
		if (result.length <= 0) {
			if (["audiooutput", "audioinput"].includes(kind)) {
				const device = this.default_audio_devices.get(kind);
				if (device) {
					result = [device];
				}
			}
			if (result.length <= 0) {
				result = this.devices.filter((item) => item.kind === kind);
			}
		}
		return result.length ? result[0] : {};
	}

	/**
	 * Resets the device states by enumerating the current devices and checking for changes.
	 *
	 * @return {Promise<void>} - A promise that resolves when the devices have been reset and changes have been processed.
	 */
	public async reset_devices(): Promise<void> {
		const devices = await this.enumerateDevices();
		this.use_debugging && console.log(LOGTAG, "reset_devices: ", devices);
		this.check_device_changes(devices);
	}

	/**
	 * 주어진 MediaStreamConstraints 객체를 기반으로 카메라와 마이크 권한 상태에 따라
	 * 미디어 제약 조건을 수정 및 반환합니다.
	 *
	 * @param {MediaStreamConstraints} constraints 입력으로 제공된 미디어 제약 조건 객체
	 * @return {MediaStreamConstraints} 수정된 미디어 제약 조건 객체
	 */
	public get_media_constraints(constraints: MediaStreamConstraints): MediaStreamConstraints {
		const { camera, microphone } = useDeviceManager.getState();
		if (camera.permission !== "granted") {
			delete constraints.video;
		}
		if (microphone.permission !== "granted") {
			delete constraints.audio;
		}

		const has_video = camera.permission === "granted" && has(constraints, "video");
		const has_audio =
			microphone.permission === "granted" && has(constraints, "audio") && !has(constraints.audio, "optional");
		const has_audio_v2 =
			microphone.permission === "granted" && has(constraints, "audio") && has(constraints.audio, "optional");
		const result = mergeWith(constraints, {
			...(has_video ? { video: { deviceId: { exact: camera.id } } } : {}),
			...(has_audio ? { audio: { deviceId: { exact: microphone.id } } } : {}),
			...(has_audio_v2
				? {
						audio: {
							// @ts-expect-error ...
							optional: [...(constraints.audio?.["optional"] ?? []), { sourceId: microphone.id }]
						}
					}
				: {})
		});
		this.use_debugging && console.log(LOGTAG, "get_media_constraints: ", result);
		return result;
	}

	/**
	 * 사용자의 미디어 장치를 기반으로 미디어 스트림을 가져옵니다.
	 *
	 * @param {MediaStreamConstraints} constraints 미디어 장치를 선택하거나 필터링하는 데 사용하는 제약 조건 객체입니다.
	 * @return {Promise<MediaStream>} 성공 시 미디어 스트림 객체를 반환하는 Promise를 반환하며, 실패 시 오류를 포함한 Promise가 반환됩니다.
	 */
	public get_user_media(constraints: MediaStreamConstraints): Promise<MediaStream> {
		try {
			const _constraints = this.get_media_constraints(constraints);
			return navigator.mediaDevices.getUserMedia(_constraints);
		} catch (error) {
			this.use_debugging && console.warn(LOGTAG, "get_user_media exception: ", error);
			return Promise.reject(error);
		}
	}
}
